/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DataList, DataListItem } from '@squiz/sds';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { CUSTOMER_PROFILE_DATA_IDS, CUSTOMER_PROFILE_EMPTY_VALUE } from '../../CustomerProfile.constants';
import { SCV_BASIC_ATTRIBUTES } from '../../attributes/ScvBasicAttributes.constants';
import { GetAttributeValueType, getAttributeValue } from '../../utils/GetAttributeValue/GetAttributeValue';
import { CustomerBasicAttributesProps } from './CustomerBasicAttributes.props';

/**
 * The content of the basic attributes panel in the customer details tab.
 */
export const CustomerBasicAttributes = (props: CustomerBasicAttributesProps): JSX.Element => {
  const { t } = useTranslation();
  const { customer, setProfileTabIndex } = props;

  // Labels
  const labels = useMemo(() => {
    return {
      basicAttributes: t(CDP_STRINGS.basicAttributes),
      viewAllAttributes: t(CDP_STRINGS.viewAllAttributes),
    };
  }, [t]);

  // Get the datalist items for the basic attributes panel
  const getItems = (): Array<DataListItem> => {
    // Map each basic attribute to a datalist item
    return SCV_BASIC_ATTRIBUTES.map((item): DataListItem => {
      const { key, title, type } = item;

      let value = CUSTOMER_PROFILE_EMPTY_VALUE;
      if (customer[key]) {
        value = getAttributeValue(customer[key] as GetAttributeValueType, type, t);
      }

      const content = (
        <div className="customer-profile-item">
          <span className="customer-profile-item__name">{title}</span>
          <span className="customer-profile-item__value">{value}</span>
        </div>
      );
      return { content, isCustom: true, key: `customer-basic-attributes-item--${key}` } as DataListItem;
    });
  };

  // Button to navigate to the attributes tab
  const viewAllAttributesButton = (
    <button
      className="customer-profile__datalist-link"
      data-id={CUSTOMER_PROFILE_DATA_IDS.viewAllAttributes}
      onClick={(): void => setProfileTabIndex(1)}
    >
      {labels.viewAllAttributes}
    </button>
  );

  return (
    <DataList
      classes="customer-basic-attributes__wrapper"
      empty={<></>}
      headerContent={viewAllAttributesButton}
      isInteractive={false}
      items={getItems()}
      title={labels.basicAttributes}
    />
  );
};
